import React from "react";
import { LineList, MultiPick, TagInput, inputCls } from "./inputs";
import { FieldGroup } from "./Cards";
import { PEDAGOGY_OPTIONS, chapterIssues } from "./planModel";

/** The fields of one chapter, as stacked sub-cards (no drawer, no modal). */
export default function ChapterFields({ chapter, locked, competencyOptions, onChange }) {
  const issues = chapterIssues(chapter);
  const set = (field, value) => onChange((c) => { c[field] = value; });
  const togglePedagogy = (p) =>
    set("pedagogies", chapter.pedagogies.includes(p) ? chapter.pedagogies.filter((x) => x !== p) : [...chapter.pedagogies, p]);

  return (
    <>
      <FieldGroup label="Chapter name" done={!issues.includes("name")}>
        <div className="grid grid-cols-[6rem_minmax(0,1fr)] gap-3">
          <input value={chapter.chapterNo} disabled={locked} onChange={(e) => set("chapterNo", e.target.value)} placeholder="No." aria-label="Chapter number" className={inputCls} />
          <input value={chapter.chapterName} disabled={locked} onChange={(e) => set("chapterName", e.target.value)} placeholder="e.g. Fractions" aria-label="Chapter name" className={inputCls} />
        </div>
      </FieldGroup>

      <FieldGroup
        label="What will students learn?"
        help="Add 2 to 4 short sentences. Start with “Students can…”."
        done={!issues.includes("objective")}
      >
        <LineList
          values={chapter.learningObjectives}
          disabled={locked}
          placeholder="Students can add two fractions"
          addLabel="Add another"
          onChange={(v) => set("learningObjectives", v)}
        />
        {!locked && chapter.learningObjectives.length === 0 && (
          <button
            type="button"
            onClick={() => set("learningObjectives", [""])}
            className="rounded-xl border-2 border-dashed border-blue-300 bg-white px-4 py-2.5 font-[poppins] text-sm font-semibold text-[#1565c0] hover:bg-blue-50"
          >
            + Write the first one
          </button>
        )}
      </FieldGroup>

      <FieldGroup
        label="Which competencies does it cover?"
        help="Search by a keyword, then tick the ones that apply."
        done={!issues.includes("competency")}
      >
        <MultiPick
          options={competencyOptions}
          selected={chapter.competencies}
          custom={chapter.customCompetencies}
          disabled={locked}
          placeholder={competencyOptions.length ? "Search competencies…" : "Type a competency and press Enter"}
          addLabel="Add your own"
          onChange={(sel, cus) => onChange((c) => { c.competencies = sel; c.customCompetencies = cus; })}
        />
      </FieldGroup>

      <FieldGroup label="How will you teach it?" help="Optional. Tap everything that fits." done={chapter.pedagogies.length + chapter.customPedagogies.length > 0}>
        <div className="flex flex-wrap gap-2">
          {PEDAGOGY_OPTIONS.map((p) => {
            const on = chapter.pedagogies.includes(p);
            return (
              <button
                key={p}
                type="button"
                disabled={locked}
                aria-pressed={on}
                onClick={() => togglePedagogy(p)}
                className={`rounded-full border-2 px-3.5 py-1.5 font-[poppins] text-sm font-medium transition ${
                  on ? "border-[#1565c0] bg-[#1565c0] text-white" : "border-gray-200 bg-white text-gray-700 hover:border-blue-400"
                } disabled:opacity-60`}
              >
                {p}
              </button>
            );
          })}
        </div>
        <div className="mt-3">
          <TagInput values={chapter.customPedagogies} disabled={locked} placeholder="Something else? Type it and press Enter" onChange={(v) => set("customPedagogies", v)} />
        </div>
      </FieldGroup>

      <FieldGroup label="Topics covered" help="Optional. Useful for lesson planning later.">
        <TagInput
          values={chapter.topics.map((t) => t.topicName)}
          disabled={locked}
          placeholder="Type a topic and press Enter"
          onChange={(names) =>
            onChange((c) => {
              const keep = new Map(c.topics.map((t) => [t.topicName, t]));
              c.topics = names.map((n) => keep.get(n) || { id: `topic_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, topicName: n, subtopics: [] });
            })
          }
        />
        {chapter.topics.length > 0 && (
          <details className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
            <summary className="cursor-pointer font-[poppins] text-sm font-semibold text-gray-700">Add sub-topics (optional)</summary>
            <div className="mt-3 space-y-3">
              {chapter.topics.map((t) => (
                <div key={t.id}>
                  <div className="mb-1 font-[poppins] text-sm font-medium text-gray-700">{t.topicName}</div>
                  <TagInput
                    values={t.subtopics}
                    disabled={locked}
                    placeholder="Sub-topics: type and press Enter"
                    onChange={(v) => onChange((c) => { c.topics.find((x) => x.id === t.id).subtopics = v; })}
                  />
                </div>
              ))}
            </div>
          </details>
        )}
      </FieldGroup>
    </>
  );
}
